<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import ExpandableContainer from '@/V2/components/expandable-container/ExpandableContainer.vue';
import ExpandableContainerItem from '@/V2/components/expandable-container/ExpandableContainerItem.vue';
import CustomerEmptyState, {
  CustomerEmptyStateSize,
} from '@/V2/customers/CustomerEmptyState.vue';
import CustomerListLoadingBars from '@/V2/customers/CustomerListLoadingBars.vue';
import {
  CustomerFraudReasoningResult,
  CustomerFraudReasoningStatusForUi,
  getCustomerFraudReasoningInsightTitle,
} from '@/utils/customerFraudReasoning';
import {
  iFeaturedIcon,
  iFeaturedIconTypes,
  iButton,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
} from '@inscribe/signal';
import { RISK_LEVEL } from '@/constants/riskLevels';
import { registerChatAgentHookClickEvent } from '@/events-logger';

interface Props {
  status: CustomerFraudReasoningStatusForUi;
  result?: CustomerFraudReasoningResult | null;
}

const props = defineProps<Props>();

const store = useStore();
const route = useRoute();

const insights = computed(() => props.result?.insights ?? []);

const showEmptyState = computed(() => {
  return props.status === CustomerFraudReasoningStatusForUi.NO_DATA;
});

const showFailedState = computed(() => {
  return props.status === CustomerFraudReasoningStatusForUi.FAILED;
});

const showLoadingState = computed(() => {
  return (
    props.status === CustomerFraudReasoningStatusForUi.PROCESSING_DOCUMENTS ||
    props.status === CustomerFraudReasoningStatusForUi.ANALYZING_CUSTOMER ||
    props.status === CustomerFraudReasoningStatusForUi.REASONING
  );
});

const showInsights = computed(() => {
  return (
    props.status === CustomerFraudReasoningStatusForUi.FINISHED &&
    insights.value.length > 0
  );
});

const isChatAgentEnabled = computed(() => store.getters.isChatAgentEnabled);
const currentConversationUuid = computed(
  () => store.getters.currentConversationUuid,
);

const getInsightIcon = (insightRiskLevel: string) => {
  return insightRiskLevel === RISK_LEVEL.HIGH
    ? 'medium_solid_alert_triangle'
    : insightRiskLevel === RISK_LEVEL.MEDIUM
      ? 'medium_solid_alert_circle'
      : 'medium_solid_check_circle';
};

const getInsightIconType = (insightRiskLevel: string) => {
  return insightRiskLevel === RISK_LEVEL.HIGH
    ? iFeaturedIconTypes.ERROR
    : insightRiskLevel === RISK_LEVEL.MEDIUM
      ? iFeaturedIconTypes.WARNING
      : iFeaturedIconTypes.SUCCESS;
};

const openChatWithInsight = (insightText: string) => {
  store.dispatch('openChat');
  store.dispatch('submitConversationMessage', {
    conversationUuid: currentConversationUuid.value,
    userMessageContent: `Tell me more about the insight for this customer "${insightText}"`,
    contextCustomerUuids: [route.params.customer_id],
    contextDocumentIds: [],
  });
  registerChatAgentHookClickEvent(
    'customer-fraud-reasoning-insight-ask-assistant',
  );
};
</script>

<template>
  <div class="reasoning-insights-list">
    <ExpandableContainer title="Insights">
      <template #items>
        <div
          class="insights-container list-groups-container d-flex flex-column"
        >
          <customer-empty-state
            v-if="showEmptyState"
            title="AI Fraud Reasoning Agent has not run yet"
            :show-button="false"
            :size="CustomerEmptyStateSize.SMALL"
            description="Please check Risk Analysis tab"
            icon="medium-file"
          />

          <customer-empty-state
            v-else-if="showFailedState"
            title="Failed to generate insights"
            :show-button="false"
            :size="CustomerEmptyStateSize.SMALL"
            description="There was an error"
            icon="medium-file"
          />
          <template v-else-if="showLoadingState">
            <div class="list-group d-flex flex-column i-gap-2 i-radius-3xl">
              <customer-list-loading-bars />
            </div>
          </template>

          <template v-else-if="showInsights">
            <div class="list-group d-flex flex-column i-gap-2 i-radius-3xl">
              <div
                class="title d-flex align-items-center justify-content-start i-px-9 i-py-5"
              >
                <div class="main i-text-subheading-3">Key Findings</div>
              </div>
              <div class="items-list d-flex flex-column">
                <expandable-container-item
                  v-for="{ insightType, insight, insightRiskLevel } in insights"
                  :key="insightType"
                  :title="getCustomerFraudReasoningInsightTitle(insightType)"
                  :subtitle="insight"
                  :is-clickable="false"
                  :has-right-icon="false"
                  class="item-card"
                >
                  <template #left-container>
                    <i-featured-icon
                      :symbol="getInsightIcon(insightRiskLevel)"
                      :type="getInsightIconType(insightRiskLevel)"
                    ></i-featured-icon>
                  </template>
                  <template v-if="isChatAgentEnabled" #hover-right-container>
                    <i-button
                      text="Ask Assistant"
                      :size="iButtonSizes.SMALL"
                      :type="iButtonTypes.NEUTRAL"
                      :button-style="iButtonStyles.OUTLINE"
                      @button-click="() => openChatWithInsight(insight)"
                    >
                      Ask Assistant
                    </i-button>
                  </template>
                </expandable-container-item>
              </div>
            </div>
          </template>
        </div>
      </template>
    </ExpandableContainer>
  </div>
</template>

<style lang="scss" scoped>
.reasoning-insights-list {
  .loading-skeleton {
    background: var(--base);
  }

  .items-list {
    background: var(--base);

    .item-card {
      :deep(.text-container-subtitle) {
        white-space: normal;
      }
    }
  }
}
</style>
